import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Delete, Download, Lock } from "lucide-react";
import { API, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function usePwaManifest(slug) {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const link = document.getElementById("app-manifest");
    const prev = link?.getAttribute("href");
    if (link) link.setAttribute("href", `${API}/public/manifest/${slug}`);
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (link && prev) link.setAttribute("href", prev);
    };
  }, [slug]);
  return prompt;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

export default function StaffPin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, applySession, logout } = useAuth();
  const [biz, setBiz] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const installPrompt = usePwaManifest(slug);

  useEffect(() => {
    api.get(`/public/business/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(false));
  }, [slug]);

  useEffect(() => {
    if (pin.length !== 6) return;
    (async () => {
      setBusy(true);
      setError("");
      try {
        if (user) await logout();
        const { data } = await api.post("/auth/pin-login", { slug, pin });
        await applySession(data);
        nav("/app/checks");
      } catch (e) {
        setError(errMsg(e));
        setPin("");
      } finally {
        setBusy(false);
      }
    })();
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  const press = (k) => {
    if (busy) return;
    setError("");
    if (k === "clear") setPin("");
    else if (k === "back") setPin((p) => p.slice(0, -1));
    else setPin((p) => (p.length < 6 ? p + k : p));
  };

  if (biz === false) return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-300">This staff link isn't valid.</div>;

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-950 px-6 pb-10 pt-[max(2.5rem,env(safe-area-inset-top))] text-white">
      <img src="/icon-192.png" alt="" className="h-14 w-14 rounded-2xl" />
      <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400">Staff app</div>
      <h1 data-testid="staff-business-name" className="mt-2 text-center text-3xl font-extrabold">{biz?.name || "…"}</h1>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-400"><Lock className="h-3.5 w-3.5" /> Enter your 6-digit PIN</p>
      <div data-testid="pin-dots" className={`mt-8 flex gap-3 ${error ? "animate-pulse" : ""}`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className={`h-4 w-4 rounded-full border-2 transition-colors duration-150 ${i < pin.length ? "border-emerald-400 bg-emerald-400" : "border-slate-600"}`} />
        ))}
      </div>
      <div data-testid="pin-error" className="mt-4 h-5 text-sm text-rose-400">{error}</div>
      <div className="mt-4 grid w-full max-w-xs grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <button key={k} data-testid={`pin-key-${k}`} onClick={() => press(k)}
            className="ka-press flex h-[72px] items-center justify-center rounded-2xl bg-white/5 font-display text-2xl font-bold hover:bg-white/10 active:bg-emerald-600">
            {k === "back" ? <Delete className="h-6 w-6" /> : k === "clear" ? <span className="text-sm font-semibold text-slate-400">Clear</span> : k}
          </button>
        ))}
      </div>
      {installPrompt && (
        <button data-testid="install-app-button" onClick={() => installPrompt.prompt()} className="ka-press mt-8 flex items-center gap-2 rounded-full border border-emerald-500/40 px-5 py-2.5 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/10">
          <Download className="h-4 w-4" /> Install on this device
        </button>
      )}
      <p className="mt-auto pt-10 text-center text-xs text-slate-500">On iPhone: tap Share → "Add to Home Screen" to install.</p>
    </div>
  );
}
